import type { TextChain } from '@/api/types/stowed-element'
import { isLinkedTextFrame } from '@/lib/groups/linked-text-group-policy'
import type { PhotoDropCandidateFrame } from '@/lib/photos/types/photo-drop-candidate-frame'

/**
 * True when this drop target belongs to a text-link chain, and so must never receive a photo
 * (WMPCM-8516).
 *
 * Filling a chain member is destructive, not merely untidy. `isShapeTextFrame`
 * (`lib/shapes/shape-text.ts`) returns false the moment a shape carries a `photoFill` — photo fill
 * and shape text are mutually exclusive on the wire and the photo wins — so the frame silently
 * drops out of `collectLinkableFrames` while the chain still lists its uid. That dangles the uid
 * (the corruption `sanitizeChains` exists to repair), destroys whatever text had flowed into the
 * frame from upstream, and breaks reflow for the rest of the chain. The Text-tool equivalent is the
 * same hazard by a different route: `applyTextToImageReplace` removes the text box outright,
 * without the chain repair the delete path performs — the reason the WMPCM-7036 guards exist.
 *
 * Delegates to `isLinkedTextFrame` rather than re-deriving the check from `chainContaining`. Two
 * copies of this predicate already exist (`isLinkedTextFrame`, and `isLinkedChainMember` in
 * `lib/editor/cut-selection-policy.ts`); a third drifting apart from them is exactly what
 * WMPCM-8969 was. This wrapper exists only to carry the photo-specific rationale above and to give
 * the hit test and the drop funnel one shared import.
 */
export function isPhotoDropBlockedByTextChain(
  frame: PhotoDropCandidateFrame,
  chains: readonly TextChain[]
): boolean {
  return isLinkedTextFrame(frame, chains)
}

/**
 * Shown when a photo is dropped on a linked text frame (WMPCM-8516). Deliberately worded to match
 * `LINKED_TEXT_GROUP_BLOCKED_MESSAGE` — both name the offending object and the one action that
 * unblocks it.
 */
export const LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE =
  'Cannot place a photo in a linked text frame. Unlink it first.'
